import React, { useState, useRef, useEffect } from 'react';
import { getAllCats, mk, fc } from '../utils';

export default function QuickAdd({ state, updateState, showToast }) {
  const [open, setOpen] = useState(false);
  const [catId, setCatId] = useState('groc');
  const [place, setPlace] = useState('');
  const [amt, setAmt] = useState('');
  const [pendingDup, setPendingDup] = useState(false);
  const [undoItem, setUndoItem] = useState(null); // { moKey, catId, ts, place, amt }
  const lastSubmitRef = useRef(null);
  const undoTimerRef = useRef(null);

  useEffect(() => () => { if (undoTimerRef.current) clearTimeout(undoTimerRef.current); }, []);

  const cats = getAllCats(state.customCats, state.disabledCats);
  const selCat = cats.find((c) => c.id === catId) || cats[0];

  const close = () => {
    setOpen(false);
    setPlace('');
    setAmt('');
    setPendingDup(false);
  };

  const bankAdjust = (delta) => {
    const bal = state.bankBalance || 0;
    if (!bal) return {};
    return {
      bankBalance: Math.max(0, Math.round((bal + delta) * 100) / 100),
      bankBalanceDate: new Date().toISOString().slice(0, 10),
    };
  };

  const commit = (trimmed, amount) => {
    const now = new Date();
    const ts = Date.now();
    const moKey = mk(now.getFullYear(), now.getMonth());
    const updated = { ...state.varSpend };
    if (!updated[moKey]) updated[moKey] = {};
    updated[moKey] = {
      ...updated[moKey],
      [catId]: [...(updated[moKey][catId] || []), { place: trimmed, amt: amount, ts, date: new Date(ts).toISOString().slice(0, 10) }],
    };
    updateState({ varSpend: updated, ...bankAdjust(-amount) });
    lastSubmitRef.current = { place: trimmed, amount, ts };
    // Show undo banner for 5 seconds
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoItem({ moKey, catId, ts, place: trimmed, amt: amount });
    undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
    close();
  };

  const handleUndo = () => {
    if (!undoItem) return;
    clearTimeout(undoTimerRef.current);
    const { moKey, catId: undoCatId, ts: undoTs } = undoItem;
    setUndoItem(null);
    lastSubmitRef.current = null;
    const updated = { ...state.varSpend };
    if (updated[moKey]?.[undoCatId]) {
      updated[moKey] = {
        ...updated[moKey],
        [undoCatId]: updated[moKey][undoCatId].filter((x) => x.ts !== undoTs),
      };
      updateState({ varSpend: updated, ...bankAdjust(undoItem.amt) }); // add back on undo
    }
    showToast('Removed!');
  };

  const submit = () => {
    const trimmed = place.trim();
    if (!trimmed) { showToast('Enter where you spent it'); return; }
    const amount = parseFloat(amt);
    if (!amount || amount <= 0) { showToast('Enter an amount'); return; }
    // Duplicate guard: same place + amount within 10 seconds
    const last = lastSubmitRef.current;
    if (last && last.place.toLowerCase() === trimmed.toLowerCase() &&
        Math.abs(last.amount - amount) < 0.01 && Date.now() - last.ts < 10000) {
      setPendingDup(true);
      return;
    }
    commit(trimmed, amount);
  };

  return (
    <>
      <button className="qa-fab" onClick={() => setOpen(true)} aria-label="Quick add expense">
        +
      </button>

      {/* Undo banner — floats above FAB for 5 seconds after adding */}
      {undoItem && !open && (
        <div style={{
          position: 'fixed', bottom: 80, right: 16, zIndex: 999,
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '10px 14px', background: 'var(--surf)',
          border: '1px solid var(--bdr)', borderRadius: 10,
          boxShadow: '0 4px 20px rgba(0,0,0,.3)', fontSize: 12,
        }}>
          <span style={{ color: 'var(--mut)' }}>
            Added <strong style={{ color: 'var(--txt)' }}>{undoItem.place}</strong> {fc(undoItem.amt)}
          </span>
          <button
            onClick={handleUndo}
            style={{
              fontWeight: 800, fontSize: 12, color: 'var(--ylw)',
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
            }}
          >
            Undo
          </button>
        </div>
      )}

      {open && (
        <div
          className="qa-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="qa-sheet">
            {pendingDup && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', marginBottom: 12, background: 'rgba(255,217,61,.1)', border: '1px solid rgba(255,217,61,.4)', borderRadius: 8, fontSize: 12, gap: 8 }}>
                <span>⚠️ <strong>{place.trim()}</strong> for ${parseFloat(amt).toFixed(2)} was just added — add again?</span>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button onClick={() => { setPendingDup(false); commit(place.trim(), parseFloat(amt)); }} style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 7, border: '1.5px solid var(--ylw)', background: 'rgba(255,217,61,.15)', color: 'var(--ylw)', cursor: 'pointer' }}>Yes</button>
                  <button onClick={() => setPendingDup(false)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 7, border: '1px solid var(--bdr)', background: 'none', color: 'var(--mut)', cursor: 'pointer' }}>No</button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 14, fontWeight: 800 }}>Quick Add Expense</span>
              <button onClick={close} style={{ background: 'none', border: 'none', color: 'var(--mut)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 0 }}>
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
              {cats.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setCatId(cat.id)}
                  style={{
                    padding: '6px 11px', borderRadius: 99,
                    border: '1.5px solid ' + (catId === cat.id ? cat.col : 'var(--bdr)'),
                    background: catId === cat.id ? 'rgba(255,255,255,.07)' : 'transparent',
                    color: catId === cat.id ? cat.col : 'var(--mut)',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  {cat.em} {cat.name}
                </button>
              ))}
            </div>

            <input
              type="text"
              placeholder="Where? (e.g. Walmart, Shell)"
              value={place}
              onChange={(e) => setPlace(e.target.value)}
              autoFocus
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8,
                border: '1px solid var(--bdr)', background: 'var(--surf2)',
                color: 'var(--txt)', fontSize: 14, fontFamily: 'inherit',
                marginBottom: 8, boxSizing: 'border-box',
              }}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surf2)', border: '1px solid var(--bdr)', borderRadius: 8, padding: '0 12px', marginBottom: 14 }}>
              <span style={{ color: 'var(--mut)', fontSize: 14, fontWeight: 600 }}>$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={amt}
                onChange={(e) => setAmt(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
                style={{ flex: 1, background: 'none', border: 'none', fontSize: 20, fontWeight: 700, color: 'var(--ylw)', padding: '10px 0', fontFamily: 'inherit', width: '100%' }}
              />
            </div>

            <button
              onClick={submit}
              style={{ width: '100%', padding: 13, borderRadius: 10, background: selCat.col || 'var(--ylw)', color: '#000', fontWeight: 800, fontSize: 14, border: 'none', cursor: 'pointer' }}
            >
              + Add {selCat.em} {selCat.name}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
